import { PlayArrow } from '@mui/icons-material';
import {
  Button,
  Collapse,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery
} from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import { theme } from '../../utils/theme';
import { CollapsibleCellStyle, CollapsibleHeaderStyle, NoRecordsStyle, PaginationStyle, TableBodyStyle, TableCellStyle, TableHeadCellStyle, TableStyle } from './styled';

interface CollapsibleTableProps<D> {
  data: D[];
  pageSize?: number;
  onLoadNextPage?: (page: number) => void;
  headerCells: string[] | JSX.Element[],
  expandedRowProp?: keyof D,
  collapsibleHeaderCells: string[],
  collapsibleColumnWidths: string[],
  currentPage?: number,
  noData?: boolean,
  customizedHeaderCells?: boolean,
  customizedDataRows?: boolean,
  size?: "custom" | "medium" | "small";
}

const CollapsibleTableWithPagination: React.FC<CollapsibleTableProps<any>> = ({
  data,
  pageSize = 10,
  onLoadNextPage,
  headerCells,
  expandedRowProp,
  collapsibleHeaderCells,
  collapsibleColumnWidths,
  currentPage = 1,
  noData,
  customizedHeaderCells = false,
  customizedDataRows = false,
  size = "medium"
}) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(currentPage);
  const [openRows, setOpenRows] = useState<Record<number, boolean>>({});
  const startIndex = pageSize * (page - 1);
  const endIndex = startIndex + pageSize;

  const onRowClick = (rowId: number) => {
    setOpenRows({ ...openRows, [rowId]: !openRows[rowId] });
  };

  const onChangePage = (e: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    if (onLoadNextPage) {
      onLoadNextPage(newPage);
    }
    setOpenRows({});
  };

  const renderHeaderCells = () => {
    if (customizedHeaderCells) {
      const cells = headerCells as JSX.Element[];
      return cells.map(hc => hc);
    } else {
      const cells = headerCells as string[];
      return <>
        <TableCell sx={TableHeadCellStyle} />
        {cells.map((hc, i) => (<TableCell sx={TableHeadCellStyle} key={i}>{hc}</TableCell>))}
      </>
    }
  }

  const renderDataRows = () => {
    if (customizedDataRows) {
      return data.slice(startIndex, endIndex).map(r => r);
    } else {
      return data.slice(startIndex, endIndex).map((row, i) => (
        <React.Fragment key={i}>
          <TableRow className={i % 2 === 0 ? 'WithBackground' : ''} data-automation={`tbl-row-${i}`}>
            <TableCell sx={{ pt: 0, pb: 0 }}>
              {row && expandedRowProp && row[expandedRowProp].length ? <Button
                variant="expand-icon"
                aria-label="expand row"
                size="small"
                onClick={() => onRowClick(i)}

                data-automation={`tbl-row-group-toggle-${i}`}
              >
                <PlayArrow className={openRows[i] ? 'open' : ''} />
              </Button> : null}
            </TableCell>
            {row &&
              Object.keys(row).reduce<React.ReactNode[]>((cells, k) => {
                if (k !== expandedRowProp) {
                  cells.push(<TableCell sx={TableCellStyle} key={k}>{(row as any)[k]}</TableCell>);
                }
                return cells;
              }, [])}
          </TableRow>
          <TableRow data-automation={`tbl-row-group-${i}`}>
            <TableCell sx={{ p: 0, borderBottomWidth: 0 }} colSpan={row && Object.keys(row).length}>
              <Collapse in={openRows[i]} timeout="auto" unmountOnExit>
                <Table>
                  <TableHead data-automation={`tbl-row-group-header-${i}`}>
                    <TableRow>
                      {
                        collapsibleHeaderCells.map((hc, i) => (
                          <TableCell sx={[CollapsibleHeaderStyle, { width: collapsibleColumnWidths[i] }]} key={hc}>
                            {hc}
                          </TableCell>
                        ))
                      }
                    </TableRow>
                  </TableHead>
                  <TableBody data-automation={`tbl-row-group-body-${i}`}>
                    {row && expandedRowProp && row[expandedRowProp].map((er: any, j: number) => (
                      <TableRow key={er.key ?? j} data-automation={`tbl-row-group-${i}-row-${j}`}>
                        {
                          er && Object.keys(er).reduce<React.ReactNode[]>((cells, k, j) => {
                            if (k !== 'key') {
                              cells.push(
                                <TableCell sx={[CollapsibleCellStyle, { width: collapsibleColumnWidths[j] }]} key={k}>
                                  {er[k]}
                                </TableCell>
                              );
                            }
                            return cells;
                          }, [])
                        }
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>
      ));
    }
  }

  if (noData || !data.length) {
    return (
      <Typography
        sx={NoRecordsStyle}
        data-automation={'no-records'}
      >
        No records
      </Typography>
    )
  }
  return (
    <>
      <Table size={size} sx={TableStyle}>
        <TableHead data-automation="tbl-header">
          <TableRow>
            {renderHeaderCells()}
          </TableRow>
        </TableHead>
        <TableBody sx={TableBodyStyle} data-automation="tbl-body">
          {renderDataRows()}
        </TableBody>
      </Table>
      <Pagination
        count={Math.ceil(data.length / pageSize)}
        page={page}
        onChange={onChangePage}
        variant="outlined"
        shape="rounded"
        size={isMobile ? 'small' : 'medium'}
        sx={PaginationStyle}
        data-automation="pagination"
      />
    </>
  );
};

export default CollapsibleTableWithPagination;
